import {chakra, type SystemStyleObject} from '@chakra-ui/react';
import type {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ChakraSpan} from '@linx-security/ui/utils/chakra-elements';
import type {ReactNode, JSX} from 'react';

import {isFontAwesomeIcon} from '../../utils/is-fa';
import type {DotProps} from '../Dot';
import {Dot} from '../Dot';

import {useStyles} from './context';
import {parts} from './theme/anatomy';

type PropsWithDot = {
  type: 'dot';
} & DotProps;

type PropsWithIcon = {
  type: 'icon';
  icon: IconDefinition;
  style?: React.CSSProperties;
  sx?: SystemStyleObject;
};

type PropsWithCustomNode = {
  type: 'custom';
  render: JSX.Element;
};

type Props = {type: 'none'} | PropsWithDot | PropsWithIcon | PropsWithCustomNode;

export const ListBullet = (props: Props) => {
  const {bullet} = useStyles();
  const {type, ...rest} = props;

  const common = {
    ...parts.bullet.attrs,
  };

  /** Renders a container to keep fixed size of the bullet    */
  const render = (children: ReactNode) => {
    return (
      <ChakraSpan __css={bullet} {...common} {...rest}>
        {children}
      </ChakraSpan>
    );
  };

  if (type === `none`) {
    return null;
  }

  if (type === `dot`) {
    return render(<Dot {...props} />);
  }

  if (type === `icon` && isFontAwesomeIcon(props.icon)) {
    return render(
      <Icon {...props} icon={props.icon} sx={props.sx} style={{...(props.style ?? {}), fontSize: `1.125rem`}} />,
    );
  }

  if (type === `custom`) {
    return render(props.render);
  }

  return null; // Fallback, should not reach here
};

export type {Props as ListBulletProps};

const Icon = chakra(FontAwesomeIcon, {});
